import React, { useState, useCallback, useRef, useEffect } from 'react';
import './GlobalTooltip.css';

export interface TooltipContent {
  title: string;
  type?: string;
  quantity?: number;
  stats?: Record<string, number>;
  rarity?: string;
  description?: string;
  additionalInfo?: React.ReactNode;
  hint?: string;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  content: TooltipContent | null;
}

interface GlobalTooltipContextType {
  showTooltip: (content: TooltipContent, x: number, y: number) => void;
  hideTooltip: () => void;
}

export const GlobalTooltipContext = React.createContext<GlobalTooltipContextType | null>(null);

export const useGlobalTooltip = (): GlobalTooltipContextType => {
  const context = React.useContext(GlobalTooltipContext);
  if (!context) {
    throw new Error('useGlobalTooltip must be used within GlobalTooltipProvider');
  }
  return context;
};

interface GlobalTooltipProviderProps {
  children: React.ReactNode;
}

export const GlobalTooltipProvider: React.FC<GlobalTooltipProviderProps> = ({ children }) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = useCallback((content: TooltipContent, x: number, y: number) => {
    setTooltip({
      visible: true,
      x,
      y,
      content,
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip({
      visible: false,
      x: 0,
      y: 0,
      content: null,
    });
  }, []);

  // Clamp tooltip position to viewport
  useEffect(() => {
    if (!tooltip.visible || !tooltipRef.current) return;

    const rect = tooltipRef.current.getBoundingClientRect();
    const tooltipWidth = rect.width;
    const tooltipHeight = rect.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;

    let nextX = tooltip.x;
    let nextY = tooltip.y;

    // Clamp horizontally
    if (nextX + tooltipWidth + padding > viewportWidth) {
      nextX = viewportWidth - tooltipWidth - padding;
    }
    if (nextX < padding) {
      nextX = padding;
    }

    // Clamp vertically
    if (nextY + tooltipHeight + padding > viewportHeight) {
      nextY = viewportHeight - tooltipHeight - padding;
    }
    if (nextY < padding) {
      nextY = padding;
    }

    if (nextX !== tooltip.x || nextY !== tooltip.y) {
      setTooltip((prev) => ({
        ...prev,
        x: nextX,
        y: nextY,
      }));
    }
  }, [tooltip.visible, tooltip.x, tooltip.y]);

  const RARITY_COLORS: Record<string, string> = {
    common: '#9e9e9e',
    uncommon: '#4caf50',
    rare: '#2196f3',
    epic: '#9c27b0',
    legendary: '#ff9800',
  };

  const STAT_LABELS: Record<string, string> = {
    attack: 'Attack',
    defense: 'Defense',
    speed: 'Speed',
    hp: 'HP',
    accuracy: 'Accuracy',
    evasion: 'Evasion',
    critChance: 'Crit Chance',
    critDamage: 'Crit Damage',
  };

  return (
    <GlobalTooltipContext.Provider value={{ showTooltip, hideTooltip }}>
      {children}
      {tooltip.visible && tooltip.content && (
        <div
          ref={tooltipRef}
          className="global-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            position: 'fixed',
            zIndex: 10000,
          }}
        >
          <div
            className="tooltip-name"
            style={{ color: tooltip.content.rarity ? RARITY_COLORS[tooltip.content.rarity] : '#fff' }}
          >
            {tooltip.content.title}
          </div>
          {tooltip.content.type && (
            <div className="tooltip-type">
              {tooltip.content.type}
            </div>
          )}
          {tooltip.content.quantity && tooltip.content.quantity > 1 && (
            <div className="tooltip-quantity">Quantity: {tooltip.content.quantity}</div>
          )}
          {tooltip.content.description && (
            <div className="tooltip-description">{tooltip.content.description}</div>
          )}
          {tooltip.content.stats && Object.keys(tooltip.content.stats).length > 0 && (
            <div className="tooltip-stats">
              {Object.entries(tooltip.content.stats).map(([key, value]) => (
                <div key={key} className="tooltip-stat">
                  {STAT_LABELS[key] || key}:{' '}
                  <span style={{ color: value > 0 ? '#2ecc71' : '#e74c3c' }}>
                    {value > 0 ? '+' : ''}
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
          {tooltip.content.additionalInfo && (
            <div className="tooltip-additional">{tooltip.content.additionalInfo}</div>
          )}
          {tooltip.content.hint && <div className="tooltip-hint">{tooltip.content.hint}</div>}
        </div>
      )}
    </GlobalTooltipContext.Provider>
  );
};
